'use client';

import { useState, useMemo } from 'react';

import { faEnvelope } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';

import Alert from '@/components/common/Alert';
import Modal from '@/components/common/Modal';
import { signupUser, verifyOTP } from '@/features/auth/authSlice';
import { RootState } from '@/store';

import Divider from './Divider';
import GoogleSignIn from './GoogleSignIn';
import OTPVerification from './OTPVerification';
import ErrorMessage from '../common/ErrorMessage';

interface SignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
}

interface SignUpFormProps {
  onSignInClick: () => void;
}

export default function SignUpForm({ onSignInClick }: SignUpFormProps) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { isLoading, error: authError } = useSelector((state: RootState) => state.auth);
  const { requestAction } = useSelector((state: RootState) => state.auth);

  const [formData, setFormData] = useState<SignUpFormData>({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const isFormValid = useMemo(() => {
    return (
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== ''
    );
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setShowAlert(false);

    try {
      await dispatch(signupUser(formData)).unwrap();
      setShowOTPVerification(true);
    } catch (err) {
      setShowAlert(true);
    }
  };

  const handleOTPVerification = async (otp: string) => {
    try {
      await dispatch(verifyOTP({ email: formData.email, otp })).unwrap();
    } catch (err) {
      setShowAlert(true);
    }
  };

  const handleSuccess = () => {
    switch (requestAction) {
      case 'SUBSCRIBE':
        router.push('/onboarding/');
        break;
      default:
        router.push('/');
    }
  };

  if (showOTPVerification) {
    return (
      <OTPVerification
        email={formData.email}
        onBack={() => setShowOTPVerification(false)}
        onVerify={handleOTPVerification}
        onSuccess={handleSuccess}
      />
    );
  }

  return (
    <>
      <div className="w-full flex flex-col items-center">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Account</h2>
          <p className="text-sm text-gray-500">
            Join us to streamline your tax compliance
          </p>
        </div>

        {/* Google Sign Up */}
        <div className="w-full mb-6">
          <GoogleSignIn text="Sign up with Google" />
        </div>

        <Divider />

        {/* Sign Up Form */}
        <form onSubmit={handleSubmit} className="w-full mt-6 space-y-5">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
              Your name
            </label>
            <div className="grid grid-cols-2 gap-3">
              <input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="First Name"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400"
              />
              <input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="Last Name"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400"
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4 text-gray-400" />
              </div>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter your email"
                className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400"
              />
            </div>
          </div>

          {showAlert && <ErrorMessage message={authError || 'Something went wrong!'} />}

          <div className="text-center text-xs text-gray-500 py-1">
            By continuing, I agree to{' '}
            <button
              type="button"
              onClick={() => setShowPrivacyModal(true)}
              className="font-medium text-[#0D60E6] hover:text-[#0D52C2]"
            >
              Privacy Policy
            </button>{' '}
            and{' '}
            <button
              type="button"
              onClick={() => setShowTermsModal(true)}
              className="font-medium text-[#0D60E6] hover:text-[#0D52C2]"
            >
              Terms of Service
            </button>
          </div>

          <button
            type="submit"
            disabled={!isFormValid || isLoading}
            className="w-full bg-[#0D60E6] text-white py-3.5 rounded-xl font-semibold hover:bg-[#0D52C2] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>

          <p className="text-center text-sm pt-2">
            <span className="text-gray-500">Already have an account? </span>
            <button
              type="button"
              onClick={onSignInClick}
              className="text-[#0D60E6] hover:text-[#0D52C2] font-semibold"
            >
              Sign in
            </button>
          </p>
        </form>
      </div>

      {/* Privacy Policy Modal */}
      <Modal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        title="Privacy Policy"
      >
        <div className="prose prose-sm max-w-none">
          <p className="mb-4">
            At Finkr, we take your privacy seriously. This Privacy Policy describes how we
            collect, use, and protect your personal information.
          </p>
          <h3 className="text-lg font-medium mb-2">Information We Collect</h3>
          <ul className="list-disc pl-5 mb-4">
            <li>Name and contact information</li>
            <li>Account credentials</li>
            <li>Usage data and preferences</li>
            <li>Tax-related information and documents</li>
          </ul>
          <h3 className="text-lg font-medium mb-2">How We Use Your Information</h3>
          <ul className="list-disc pl-5 mb-4">
            <li>Provide and improve our tax compliance services</li>
            <li>Process your tax documents and submissions</li>
            <li>Communicate important updates and notifications</li>
            <li>Ensure compliance with applicable laws and regulations</li>
          </ul>
          <h3 className="text-lg font-medium mb-2">Data Security</h3>
          <p className="mb-4">
            We implement industry-standard security measures to protect your personal and financial
            information. This includes encryption, secure data storage, and regular security audits.
          </p>
          <p>For more information about our privacy practices, please contact our support team.</p>
        </div>
      </Modal>

      {/* Terms of Service Modal */}
      <Modal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        title="Terms of Service"
      >
        <div className="prose prose-sm max-w-none">
          <p className="mb-4">
            By using Finkr, you agree to these Terms of Service. Please read them carefully before
            proceeding.
          </p>
          <h3 className="text-lg font-medium mb-2">Your Responsibilities</h3>
          <ul className="list-disc pl-5 mb-4">
            <li>Provide accurate and complete information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Comply with all applicable tax laws and regulations</li>
            <li>Keep your contact information up to date</li>
          </ul>
          <h3 className="text-lg font-medium mb-2">Our Services</h3>
          <ul className="list-disc pl-5 mb-4">
            <li>Tax compliance assistance and document processing</li>
            <li>Secure storage of tax-related information</li>
            <li>Updates and notifications about tax compliance requirements</li>
            <li>Technical support for platform-related issues</li>
          </ul>
          <h3 className="text-lg font-medium mb-2">Limitations of Liability</h3>
          <p className="mb-4">
            While we strive to provide accurate and reliable services, we are not responsible for
            any errors or omissions in your tax submissions. You are ultimately responsible for
            ensuring the accuracy of your tax compliance documents.
          </p>
          <p>
            We reserve the right to modify these terms at any time. Continued use of the service
            constitutes acceptance of any changes.
          </p>
        </div>
      </Modal>
    </>
  );
}
